import { useEffect, useRef, useState } from "react"
import EmptyFolder from 'assets/img/app/empty-folder.png';
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, Pagination, CircularProgress, TextField, Card, CardHeader, CardContent, Collapse, IconButton, Typography, Chip, Button, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, List, ListItem, ListItemText, ListItemSecondaryAction, InputAdornment, Avatar, Stack, Box } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ImageIcon from '@mui/icons-material/Image';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TvIcon from '@mui/icons-material/Tv';
import { ImageVisualizer, SidebarMenu, SolidButton } from "components";
import { formatDateToISODateString, formatDateToLocaleDateString, formatISODateStringToDate } from "utils";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { useAppSelector } from 'hooks';


export const GondolaColdPage = () => {
    const today = new Date();
    const { token } = useAppSelector(state => state.user);

    // Calcular la fecha mínima del sistema (6 meses atrás)
    const systemMin = (() => {
        const d = new Date(today);
        d.setMonth(d.getMonth() - 6);
        return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    })();

    // Inicializar el rango por defecto (último mes)
    const defaultStart = (() => {
        const d = new Date(today);
        d.setMonth(d.getMonth() - 1);
        return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    })();

    // estados para almacenar los datos del reporte
    const [reports, setReports] = useState<any[]>([]);
    const [backupReports, setBackupReports] = useState<any[]>([]);

    // estados para manejar la paginación
    const [paginationCount, setPaginationCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const recordsPerView = 10

    // estados para manejar las pantallas de carga
    const [loading, setLoading] = useState<boolean>(false);

    // estado que almacena el inicio del rango de fechas de los benchmarks consultadas
    const [startDate, setStartDate] = useState<Date>(defaultStart);
    const [finishDate, setFinishDate] = useState<Date>(new Date());

    // estado para almacenar el arreglo de imágenes que se mostrarán en pantalla
    const [imagesToShow, setImagesToShow] = useState<string[]>([]);
    const [showImageModal, setShowImageModal] = useState<boolean>(false);

    // estados para almacenar los valores que aparecerán en los filtros de búsqueda
    const [searchFilters, setSearchFilters] = useState<{ user: any, sucursal: any }>({
        user: [],
        sucursal: []
    });

    const contentRef = useRef<HTMLDivElement>(null);
    const [expandedReports, setExpandedReports] = useState<string[]>([]);
    const [sucursalSearch, setSucursalSearch] = useState<string>('');
    const [sucursalPage, setSucursalPage] = useState<number>(1);
    const sucursalsPerPage = 6;
    const [displaySearch, setDisplaySearch] = useState<string>('');
    const [displayPage, setDisplayPage] = useState<number>(1);
    const displaysPerPage = 6;

    const toggleReportExpand = (key: string) => {
        setExpandedReports(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    }

    const MAX_RANGE_MONTHS = 6;

    const addMonths = (date: Date, months: number) => {
        const d = new Date(date.getTime());
        const day = d.getDate();
        d.setMonth(d.getMonth() + months);
        if (d.getDate() < day) {
            d.setDate(0);
        }
        return d;
    }

    const normalizeWithAnchor = (start: Date, end: Date, anchor: 'start' | 'end') => {
        let s = start;
        let e = end;

        if (anchor === 'start') {
            const maxEnd = addMonths(s, MAX_RANGE_MONTHS);
            const cappedMaxEnd = maxEnd > today ? today : maxEnd;
            if (e < s) e = cappedMaxEnd;
            if (e > cappedMaxEnd) e = cappedMaxEnd;
        } else {
            // ensure start is valid (>= systemMin) and end - start <= MAX_RANGE_MONTHS
            const minStart = addMonths(e, -MAX_RANGE_MONTHS);
            const cappedMinStart = minStart < systemMin ? systemMin : minStart;

            // Si start es mayor que end, ajustarlo
            if (s > e) {
                s = cappedMinStart;
            } else if (s < cappedMinStart) {
                // Solo ajustar si el rango excede los 6 meses
                s = cappedMinStart;
            }
            // Si start >= systemMin y está dentro del rango de 6 meses, mantenerlo
        }

        return [s, e] as const;
    }

    const getBenchmarkDocs = async (signal: AbortSignal) => {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_SERVICE_URL}/benchmark/get/report`, {
            params: {
                startDate: `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`,
                endDate: `${finishDate.getFullYear()}-${(finishDate.getMonth() + 1).toString().padStart(2, '0')}-${finishDate.getDate().toString().padStart(2, '0')}`,
            },
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `UserToken ${token}`
            },
            signal: signal
        })

        const benchmarksData = response.data?.data?.gondolaAndCold || [];

        setPaginationCount(Math.ceil(benchmarksData.length / recordsPerView));
        setCurrentPage(1);
        setReports(benchmarksData)
        setBackupReports(benchmarksData)

        setSearchFilters(() => {
            let user = response.data?.data?.accounts || [];
            let sucursal = response.data?.data?.sucursals || [];
            user = user.map((account: any) => ({ ...account, checked: true }))
            sucursal = sucursal.map((suc: any) => ({ ...suc, checked: true }))
            return {
                sucursal: [{
                    key: 'all',
                    name: 'Todas las sucursales',
                    checked: true
                }, ...sucursal],
                user: [{
                    key: 'all',
                    name: 'Todos los displays',
                    lastName: '',
                    checked: true
                }, ...user]
            }
        })
        setLoading(false);
    }

    useEffect(() => {
        const controller = new AbortController();
        getBenchmarkDocs(controller.signal);
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changePaginationHandler = (event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    const changeSearchFilterHandler = (item: any, filterType: 'user' | 'sucursal') => {

        setSearchFilters((prevFilters) => {
            let updatedFilterItems = prevFilters[filterType].map((filterItem: any) => {
                if (filterItem.key === item.key) {
                    return { ...filterItem, checked: !filterItem.checked };
                }
                return filterItem;
            });

            if (item.key === 'all') {
                updatedFilterItems = updatedFilterItems.map((filterItem: any) => ({ ...filterItem, checked: !item.checked }));
                setReports(() => item.checked ? [] : backupReports);
            } else {
                setReports(() => {
                    const filteredReports = backupReports.filter((r: any) => {
                        const filterItem = updatedFilterItems.find((fi: any) => fi.key === r[filterType === 'user' ? 'displayKey' : 'sucursalId']);
                        return filterItem ? filterItem.checked : false;
                    });
                    return filteredReports;
                })
            }
            return {
                ...prevFilters,
                [filterType]: updatedFilterItems
            };
        })
    }

    const showImageHandler = (images: any[]) => {
        const imageUrls = images.map((img: any) => img.url);
        setImagesToShow(imageUrls);
        setShowImageModal(true);
    }

    return (
        <>
            <div ref={contentRef}>
                <div className="w-full h-screen grid grid-cols-12">
                    {/* Menú lateral */}
                    <div className="col-span-2 border-r-2 border-light">
                        <SidebarMenu pageName="gondola_cold" />
                    </div>

                    <div className="w-full h-screen flex flex-col col-span-10">
                        <div className="w-full flex justify-between border-b-2 border-tertiary px-2 py-3">
                            <p className="text-primary font-bold text-lg">Reporte de Góndola y Frío</p>

                            <p className="font-semibold text-lg">Del {formatDateToLocaleDateString(startDate)} al {formatDateToLocaleDateString(finishDate)}</p>
                        </div>

                        <div id="main-section" className="w-full h-screen overflow-y-scroll grid grid-cols-10">
                            {
                                loading ?
                                    <div className="col-span-10 w-full flex flex-col items-center justify-center">
                                        <CircularProgress size={50} color="primary" />
                                    </div>
                                    :
                                    <div className="w-full flex flex-col flex-grow col-span-7">
                                        {
                                            loading ?
                                                <div className="w-full h-screen flex flex-col items-center justify-center">
                                                    <CircularProgress size={50} color="primary" />
                                                </div>
                                                :
                                                reports.length === 0 ?
                                                    <div className="h-full grid grid-cols-8">
                                                        <div className="col-span-8 flex flex-col items-center gap-4 p-4">
                                                            <img src={EmptyFolder} alt='empty' className="h-[200px] object-contain" />

                                                            <p className="text-xl text-center font-semibold text-primary">No se encontraron resultados para tu búsqueda</p>
                                                        </div>
                                                    </div>
                                                    :
                                                    <div className="w-full h-full flex flex-col gap-2">
                                                        <div className="w-full flex flex-col px-2">
                                                            {
                                                                reports.map((item: any, i: number) => (
                                                                    i >= (recordsPerView * currentPage - recordsPerView) && i < (recordsPerView * currentPage) ?
                                                                        <Card key={item.key} className="mb-3" variant="outlined">
                                                                            <CardHeader
                                                                                avatar={
                                                                                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                                                                                        <TvIcon fontSize="small" />
                                                                                    </Avatar>
                                                                                }
                                                                                title={<Stack direction="row" spacing={1} alignItems="center"><Typography className="font-semibold">{item.display}</Typography><Chip label={item.records?.length ?? 0} size="small" color="info" /></Stack>}
                                                                                subheader={<Stack direction="row" spacing={1} alignItems="center"><CalendarTodayIcon fontSize="small" /> <Typography className="text-sm">{item.created}</Typography> <LocationOnIcon fontSize="small" /> <Typography className="text-sm">{item.sucursal}</Typography></Stack>}
                                                                                action={
                                                                                    <div className="flex items-center gap-2">
                                                                                        <Chip label={item.finishedAt ? 'Finalizado' : 'En progreso'} color={item.finishedAt ? 'success' : 'warning'} size="small" />
                                                                                        <IconButton onClick={() => toggleReportExpand(item.key)}>
                                                                                            <ExpandMoreIcon />
                                                                                        </IconButton>
                                                                                    </div>
                                                                                }
                                                                            />

                                                                            <Collapse in={expandedReports.includes(item.key)} timeout="auto" unmountOnExit>
                                                                                <CardContent>
                                                                                    <TableContainer component={Paper} variant="outlined">
                                                                                        <Table size="small" aria-label={`records-table-${item.key}`}>
                                                                                            <TableHead>
                                                                                                <TableRow>
                                                                                                    <TableCell>Fecha creación</TableCell>
                                                                                                    <TableCell>Fecha inicio</TableCell>
                                                                                                    <TableCell>Fecha fin</TableCell>
                                                                                                    <TableCell>Sección</TableCell>
                                                                                                    <TableCell>Subsección</TableCell>
                                                                                                    <TableCell><Stack direction="row" spacing={1} alignItems="center"><ImageIcon fontSize="small" /></Stack></TableCell>
                                                                                                    <TableCell align="center"><LocationOnIcon fontSize="small" /></TableCell>
                                                                                                </TableRow>
                                                                                            </TableHead>

                                                                                            <TableBody>
                                                                                                {item.records?.map((record: any, index: number) => (
                                                                                                    <TableRow key={item.key + index} hover>
                                                                                                        <TableCell>{record.created}</TableCell>
                                                                                                        <TableCell>{record.createdComplete}</TableCell>
                                                                                                        <TableCell>{record.finishedAt}</TableCell>
                                                                                                        <TableCell>{record.section}</TableCell>
                                                                                                        <TableCell>{record.subsection}</TableCell>
                                                                                                        <TableCell>
                                                                                                            {record.images.length > 0 ? (
                                                                                                                <Button variant="contained" color="primary" size="small" onClick={() => showImageHandler(record.images)}>
                                                                                                                    {record.images.length > 1 ? "Ver imágenes" : "Ver imagen"}
                                                                                                                </Button>
                                                                                                            ) : (
                                                                                                                <Typography variant="body2" color="textSecondary">No hay imagen</Typography>
                                                                                                            )}
                                                                                                        </TableCell>
                                                                                                        <TableCell align="center">
                                                                                                            {record.showModalValidate ? <CheckCircleIcon sx={{ color: 'success.main' }} /> : <CancelIcon sx={{ color: 'error.main' }} />}
                                                                                                        </TableCell>
                                                                                                    </TableRow>
                                                                                                ))}
                                                                                            </TableBody>
                                                                                        </Table>
                                                                                    </TableContainer>
                                                                                </CardContent>
                                                                            </Collapse>
                                                                        </Card>
                                                                        :
                                                                        null
                                                                ))
                                                            }
                                                        </div>
                                                    </div>
                                        }

                                        <div className={`w-full flex justify-center mt-auto py-4`}>
                                            <Pagination
                                                size="large"
                                                color="primary"
                                                count={paginationCount}
                                                defaultPage={1}
                                                page={currentPage}
                                                siblingCount={1}
                                                boundaryCount={2}
                                                onChange={changePaginationHandler}
                                            />
                                        </div>
                                    </div>
                            }

                            {/* Filtros de búsqueda */}
                            {
                                !loading &&
                                <Box sx={{ gridColumn: 'span 3', overflowY: 'auto', display: 'flex', p: 2, flexDirection: 'column', gap: 1.5, borderLeft: 2, borderColor: '#EBEBEB', bgcolor: '#fafbfc' }}>
                                    <Accordion sx={{
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        '&:before': { display: 'none' },
                                        borderRadius: '8px !important',
                                        overflow: 'hidden',
                                        border: '1px solid #e0e0e0'
                                    }}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ color: '#183555' }} />}
                                            aria-controls="panel1a-content"
                                            id="panel1a-header"
                                            sx={{ bgcolor: '#f5f7fa' }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CalendarTodayIcon fontSize="small" sx={{ color: '#1565c0' }} />
                                                <Typography variant="body1" fontWeight="600" color="#183555">Fecha</Typography>
                                            </Stack>
                                        </AccordionSummary>

                                        <AccordionDetails>
                                            <Stack spacing={3} alignItems="center">
                                                <Box sx={{ width: '100%' }}>
                                                    <Typography variant="subtitle2" fontWeight="600" gutterBottom>Fecha de inicio</Typography>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="date"
                                                        value={formatDateToISODateString(startDate)}
                                                        inputProps={{ min: formatDateToISODateString(systemMin), max: formatDateToISODateString(finishDate) }}
                                                        onChange={(e: any) => {
                                                            const selectedDate = e.target.value ? formatISODateStringToDate(e.target.value, false) : new Date();
                                                            if (!selectedDate) return;
                                                            const [s, f] = normalizeWithAnchor(selectedDate, finishDate, 'start');
                                                            setStartDate(s ?? new Date());
                                                            setFinishDate(f ?? new Date());
                                                        }}
                                                    />
                                                </Box>

                                                <Box sx={{ width: '100%' }}>
                                                    <Typography variant="subtitle2" fontWeight="600" gutterBottom>Fecha de fin</Typography>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="date"
                                                        value={formatDateToISODateString(finishDate)}
                                                        inputProps={{ min: formatDateToISODateString(systemMin), max: formatDateToISODateString(addMonths(startDate, MAX_RANGE_MONTHS) > today ? today : addMonths(startDate, MAX_RANGE_MONTHS)) }}
                                                        onChange={(e: any) => {
                                                            const selectedDate = e.target.value ? formatISODateStringToDate(e.target.value, false) : new Date();
                                                            if (!selectedDate) return;
                                                            const [s, f] = normalizeWithAnchor(startDate, selectedDate, 'end');
                                                            setStartDate(s ?? new Date());
                                                            setFinishDate(f ?? new Date());
                                                        }}
                                                    />
                                                </Box>

                                                <Box sx={{ width: '100%' }}>
                                                    <SolidButton
                                                        text="Aplicar"
                                                        backgroundColor="primary"
                                                        disabled={false}
                                                        onClickHandler={() => {
                                                            const controller = new AbortController();
                                                            getBenchmarkDocs(controller.signal);
                                                            return () => controller.abort();
                                                        }}
                                                    />
                                                </Box>
                                            </Stack>
                                        </AccordionDetails>
                                    </Accordion>

                                    <Accordion sx={{
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        '&:before': { display: 'none' },
                                        borderRadius: '8px !important',
                                        overflow: 'hidden',
                                        border: '1px solid #e0e0e0'
                                    }}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ color: '#183555' }} />}
                                            aria-controls="panel1a-content"
                                            id="panel1a-header"
                                            sx={{ bgcolor: '#f5f7fa' }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <PersonIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                                                <Typography variant="body1" fontWeight="600" color="#183555">Displays</Typography>
                                                <Chip
                                                    label={searchFilters.user.length}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#e8f5e9',
                                                        color: '#2e7d32',
                                                        fontWeight: 600,
                                                        border: '1px solid #a5d6a7'
                                                    }}
                                                />
                                            </Stack>
                                        </AccordionSummary>

                                        <AccordionDetails>
                                            <div className="flex flex-col">
                                                {searchFilters.user.length === 0 ? (
                                                    <p className="font-semibold text-primary">No hay datos disponibles</p>
                                                ) : (
                                                    <>
                                                        <TextField
                                                            placeholder="Buscar display..."
                                                            size="small"
                                                            value={displaySearch}
                                                            onChange={(e) => { setDisplaySearch(e.target.value); setDisplayPage(1); }}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <SearchIcon />
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                            fullWidth
                                                            className="mb-2"
                                                        />

                                                        <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                                                            {(() => {
                                                                const filtered = searchFilters.user.filter((u: any) => (u.name + ' ' + (u.lastName || '')).toLowerCase().includes(displaySearch.toLowerCase()) || u.key === 'all');
                                                                const pageCount = Math.max(1, Math.ceil(filtered.length / displaysPerPage));
                                                                const start = (displayPage - 1) * displaysPerPage;
                                                                const pageItems = filtered.slice(start, start + displaysPerPage);

                                                                return (
                                                                    <>
                                                                        {pageItems.map((item: any) => (
                                                                            <ListItem key={item.key} divider>
                                                                                <Avatar sx={{ bgcolor: 'primary.main', width: 30, height: 30, fontSize: '0.875rem', marginRight: '8px' }}>
                                                                                    {item.name.charAt(0).toUpperCase()}
                                                                                    {item.lastName ? item.lastName.charAt(0).toUpperCase() : ''}
                                                                                </Avatar>
                                                                                <ListItemText primary={item.name + (item.lastName ? ' ' + item.lastName : '')} />
                                                                                <ListItemSecondaryAction>
                                                                                    <Checkbox
                                                                                        id={item.key}
                                                                                        name={item.name + (item.lastName ? ' ' + item.lastName : '')}
                                                                                        value={item.key}
                                                                                        checked={item.checked}
                                                                                        onChange={() => changeSearchFilterHandler(item, 'user')}
                                                                                    />
                                                                                </ListItemSecondaryAction>
                                                                            </ListItem>
                                                                        ))}

                                                                        {pageCount > 1 && (
                                                                            <div className="w-full flex justify-center py-2">
                                                                                <Pagination size="small" count={pageCount} page={displayPage} onChange={(_, p) => setDisplayPage(p)} />
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()}
                                                        </List>
                                                    </>
                                                )}
                                            </div>
                                        </AccordionDetails>
                                    </Accordion>

                                    <Accordion sx={{
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        '&:before': { display: 'none' },
                                        borderRadius: '8px !important',
                                        overflow: 'hidden',
                                        border: '1px solid #e0e0e0'
                                    }}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ color: '#183555' }} />}
                                            aria-controls="panel1a-content"
                                            id="panel1a-header"
                                            sx={{ bgcolor: '#f5f7fa' }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <StoreIcon fontSize="small" sx={{ color: '#f57c00' }} />
                                                <Typography variant="body1" fontWeight="600" color="#183555">Sucursales</Typography>
                                                <Chip
                                                    label={searchFilters.sucursal.length}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#fff3e0',
                                                        color: '#f57c00',
                                                        fontWeight: 600,
                                                        border: '1px solid #ffcc80'
                                                    }}
                                                />
                                            </Stack>
                                        </AccordionSummary>

                                        <AccordionDetails>
                                            <div className="flex flex-col">
                                                {searchFilters.sucursal.length === 0 ? (
                                                    <p className="font-semibold text-primary">No hay datos disponibles</p>
                                                ) : (
                                                    <>
                                                        <TextField
                                                            placeholder="Buscar sucursal..."
                                                            size="small"
                                                            value={sucursalSearch}
                                                            onChange={(e) => { setSucursalSearch(e.target.value); setSucursalPage(1); }}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <SearchIcon />
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                            fullWidth
                                                            className="mb-2"
                                                        />

                                                        <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
                                                            {(() => {
                                                                const filtered = searchFilters.sucursal.filter((s: any) => s.name.toLowerCase().includes(sucursalSearch.toLowerCase()) || s.key === 'all');
                                                                const pageCount = Math.max(1, Math.ceil(filtered.length / sucursalsPerPage));
                                                                const start = (sucursalPage - 1) * sucursalsPerPage;
                                                                const pageItems = filtered.slice(start, start + sucursalsPerPage);

                                                                return (
                                                                    <>
                                                                        {pageItems.map((item: any) => (
                                                                            <ListItem key={item.key} divider>
                                                                                <ListItemText primary={item.name} />
                                                                                <ListItemSecondaryAction>
                                                                                    <Checkbox
                                                                                        id={item.key}
                                                                                        name={item.name}
                                                                                        value={item.key}
                                                                                        checked={item.checked}
                                                                                        onChange={() => changeSearchFilterHandler(item, 'sucursal')}
                                                                                    />
                                                                                </ListItemSecondaryAction>
                                                                            </ListItem>
                                                                        ))}

                                                                        {pageCount > 1 && (
                                                                            <div className="w-full flex justify-center py-2">
                                                                                <Pagination size="small" count={pageCount} page={sucursalPage} onChange={(_, p) => setSucursalPage(p)} />
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()}
                                                        </List>
                                                    </>
                                                )}
                                            </div>
                                        </AccordionDetails>
                                    </Accordion>
                                </Box>
                            }
                        </div>
                    </div>
                </div>
            </div>

            {
                showImageModal &&
                <>
                    <ImageVisualizer imageArray={imagesToShow} imageAlt="prueba" clickOutsideHandler={() => setShowImageModal(false)} />
                </>
            }
        </>
    )
}